import { Box, Grid, Hidden, Typography } from '@mui/material'
import { styled, Theme } from '@mui/material/styles'
import MultiAccountBadge from 'components/badges/MultiAccountBadge'
import Button from 'components/buttons/Button'
import IconButton from 'components/buttons/IconButton'
import Card from 'components/cards/Card'
import Select from 'components/form/Select'

import Avatar from '../badges/Avatar'
import { APP_COLOR } from 'utils/colors'
import i18n from 'i18n'
import { isMobileDevice } from 'utils/constants'
import {
  monthViewIcon,
  dayViewIcon,
  weekViewIcon,
  listViewIcon,
  oneDayListIcon
} from 'assets/img'
import { isGuest } from 'utils/user'

// Define your screen size variable
export const screenLgMin = isGuest() ? '951px' : '1301px'

export const Calendar = styled(Card)(() => ({
  flex: 1,
  width: '100%',
  height: '100%',
  display: 'flex',
  overflow: 'hidden',
  flexDirection: 'column'
}))

export const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',

  width: '100%',
  overflow: 'hidden',

  borderBottom: '1px solid',
  borderColor: theme.palette.border.primary
}))

export const CalendarHeaderInfo = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 0',
  minWidth: 60,
  width: 0,
  overflow: 'hidden',
  '@media (max-width: 900px)': {
    width: '100%',
    flex: '0 1 auto',
    minWidth: 'auto',
    overflow: 'visible'
  }
}))

export const CalendarHeaderTitleContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden'
}))

interface CalendarHeaderTitleProps {
  theme?: Theme
  fontSize?: string
}

export const CalendarHeaderTitle = styled('div')(
  ({ theme, fontSize }: CalendarHeaderTitleProps) => ({
    fontSize: fontSize ?? '1.625rem',
    fontWeight: 500,
    width: '100%',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    overflow: 'hidden',
    color: theme?.palette.text.primary,
    minHeight: '24px',
    '&:hover': {
      textDecoration: 'underline',
      textDecorationThickness: '0.5px',
      textDecorationColor: '#e4e4e4',
      textUnderlineOffset: '2px'
    }
  })
)

export const CalendarHeaderSubtitle = styled('span')(({ theme, color }) => ({
  fontSize: '0.9rem',
  fontWeight: 400,
  width: '100%',
  display: 'block',
  color: color ?? theme.palette.input.placeholder,
  cursor: 'pointer',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  '&:hover': {
    textDecoration: 'underline',
    textDecorationColor: '#e4e4e4',
    textUnderlineOffset: '2px'
  }
}))

export const CalendarHeaderActions = styled(Box)(() => ({
  display: 'flex',
  zIndex: 20,
  flexShrink: 0,

  '@media (max-height: 500px)': {
    height: '38px !important'
  }
}))

export const CalendarHeaderActionsCell = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  position: 'relative'
}))

export const SelectContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  position: 'relative',
  padding: theme.spacing(0, 1),

  '&:last-child': {
    padding: theme.spacing(0, 0, 0, 5),
    '&::after': {
      display: 'none'
    }
  },

  '& button[data-button="icon-button"]': {
    margin: theme.spacing(0, 2.5, 0, 0),
    '&:last-child': {
      margin: theme.spacing(0)
    }
  }
}))

interface RangeSelectProps {
  showIcon?: boolean
  customIcon?: string | typeof monthViewIcon
}

export const RangeSelect = styled(Select, {
  shouldForwardProp: (prop) =>
    !['showIcon', 'customIcon'].includes(prop as string)
})<RangeSelectProps>(({ theme, showIcon = true, customIcon }) => ({
  '[class*="-singleValue"]': {
    display: showIcon ? 'none' : 'inherit',
    maxWidth: 'none',
    ...(showIcon && {
      [`@media (min-width: ${screenLgMin})`]: {
        display: 'inherit'
      },
      '@media (max-height: 500px)': {
        display: 'none'
      }
    })
  },
  '[class*="-control"]': {
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.background.paper
    },
    '&:active, &:focus': {
      background: 'rgba(15, 127, 235, 0.1)'
    },
    '& > div:first-of-type': {
      position: showIcon ? 'relative' : 'inherit',
      padding: '2px 0.5rem',
      ...(showIcon && {
        width: '35px',
        justifyContent: 'center',
        [`@media (min-width: ${screenLgMin})`]: {
          position: 'inherit',
          width: 'auto',
          padding: '2px 0.5rem'
        },
        '@media (max-height: 500px)': {
          width: '35px',
          justifyContent: 'center'
        }
      }),
      ...(showIcon && {
        '&::before': {
          background: `url(${customIcon || monthViewIcon}) no-repeat center`,
          backgroundSize: 'contain',
          content: '""',
          display: 'inline-block',
          height: '20px',
          marginTop: '4px',
          width: '20px',

          [`@media (min-width: ${screenLgMin})`]: {
            display: 'none'
          },
          '@media (max-height: 500px)': {
            display: 'inline-block !important'
          }
        }
      })
    },
    '[class*="-IndicatorsContainer"]': showIcon
      ? {
          display: 'flex',
          [`@media (min-width: ${screenLgMin})`]: {
            display: 'inherit'
          },
          '@media (max-height: 500px)': {
            display: 'flex'
          },
          '& svg': {
            width: '12px',
            height: '12px',
            marginTop: '4px'
          }
        }
      : {
          display: 'inherit'
        },
    '.filter__indicators': showIcon
      ? {
          '@media (max-width: 900px)': {
            display: 'none'
          },
          '@media (max-height: 500px)': {
            display: 'none'
          }
        }
      : {}
  },
  ...(showIcon &&
    !customIcon && {
      '&.CALENDAR_RANGE\\/DAY': {
        '[class*="-control"] > div:first-of-type::before': {
          background: `url(${dayViewIcon}) no-repeat`
        }
      },
      '&.CALENDAR_RANGE\\/WEEK': {
        '[class*="-control"] > div:first-of-type::before': {
          background: `url(${weekViewIcon}) no-repeat`
        }
      },
      '&.CALENDAR_RANGE\\/LISTWEEK': {
        '[class*="-control"] > div:first-of-type::before': {
          background: `url(${listViewIcon}) no-repeat`
        }
      },
      '&.CALENDAR_RANGE\\/LISTDAY': {
        '[class*="-control"] > div:first-of-type::before': {
          background: `url(${oneDayListIcon}) no-repeat`
        }
      }
    })
}))

export const IconButtonSlide = styled(IconButton)(() => ({
  '&[data-prev]': {
    '& svg': {
      transform: 'rotate(90deg) translateY(1px)'
    }
  },
  '&[data-next]': {
    '& svg': {
      transform: 'rotate(-90deg) translateY(1px)'
    }
  }
}))

export const AllAccountBadge = styled(MultiAccountBadge)(({ theme }) => ({
  padding: theme.spacing(0, 3, 0, 0)
}))

export const LocationLabel = styled('div')(({ theme }) => ({
  fontWeight: 300,
  fontSize: 15
}))

export const AvatarBadge = styled(Avatar)(({ theme }) => ({}))

export const CalendarView = styled(Grid)(() => ({
  flex: 1,
  height: '100%',
  position: 'relative'
}))

export const CalendarViewScrollContainer = styled(Grid)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%'
}))

export const CalendarViewScroll = styled(Grid)(() => ({
  width: '100%',
  height: '100%'
}))

export const AddEventButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  bottom: 20,
  left: i18n.dir() === 'ltr' ? 20 : undefined,
  right: i18n.dir() === 'ltr' ? undefined : 20,
  zIndex: 1001,

  '& svg': {
    width: 32,
    height: 32,
    marginRight: 0
  }
}))

export const ListViewScrollable = styled('div')(({ theme }) => ({
  overflow: 'auto',
  height: '100%',
  zIndex: 1
}))

export const DateHeader = styled('div')<{ isFirst: boolean; isToday: boolean }>(
  ({ theme, isFirst, isToday }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    height: 40,
    width: '100%',
    padding: theme.spacing(0, 4),
    borderBottom: '1px solid #e4e4e4',
    borderTop: '1px solid #e4e4e4',
    background: isToday
      ? APP_COLOR
      : 'var(--gradient-22, linear-gradient(119deg, #F9F9F9 10.89%, #EFEEEE 97.51%))',
    borderColor: theme.palette.border.primary
  })
)

export const EventRow = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  flexDirection: 'row',
  height: 50,
  width: '100%',
  margin: theme.spacing(1, 0),
  paddingRight: i18n.dir() === 'ltr' ? 16 : undefined,
  paddingLeft: i18n.dir() === 'ltr' ? undefined : 16
}))

export const TimeColumn = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  height: '100%',
  minWidth: 90,
  maxWidth: 90,
  fontSize: 14,
  padding: theme.spacing(0, 2)
}))

export const TimeContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  height: '100%',
  width: '100%'
}))

export const TimeLabel = styled('div')(({ theme }) => ({
  width: '100%',
  textAlign: i18n.dir() === 'ltr' ? 'end' : 'start'
}))

export const NoItems = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  color: theme.palette.text.disabled,
  textAlign: 'center',
  width: '100%',
  padding: 16,
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
}))

export const TodayButton = styled(Box)<{ isCondensedWidth?: boolean }>(
  ({ theme, isCondensedWidth }) => ({
    background: '#ffffff',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    fontFamily: 'inherit',
    justifyItems: 'center',
    alignItems: 'center',
    borderRadius: '68px',
    fontSize: '19px',
    height: '52px',
    lineHeight: '51px',
    width: 'auto',
    padding: '0 4px',
    border: 'none',
    boxShadow: 'none',
    ...(!isCondensedWidth && {
      width: '125px',
      border: '1px solid #e4e4e4',
      boxShadow: '1px 1px 6px rgba(50, 50, 71, 0.05)'
    }),
    '@media (max-height: 500px)': {
      height: '30px !important',
      width: '30px !important',
      border: 'none',
      boxShadow: 'none'
    },
    '&:hover': {
      background: 'rgba(15, 127, 235, 0.05)'
    },
    '&:active': {
      background: 'rgba(15, 127, 235, 0.1)'
    },
    '& .today-text': {
      color: '#000000',
      display: 'none',
      fontWeight: 500,
      ...(!isCondensedWidth && {
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
        flex: 1
      }),
      '@media (max-height: 500px)': {
        display: 'none'
      }
    },
    '& .today-image': {
      '@media (max-height: 500px)': {
        display: 'flex'
      }
    }
  })
)
